import { CustomerIntroductionStatus } from "@/components/quotes/ops/CustomerIntroductionStatus";
/** SUNTRONIX ONE — Customer 360 (internal staff account page).
 *  Route: /commercial/crm/customers/:id — one page combining the account's
 *  timeline, quotes, orders, POs, discussion and One Sidekick. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { TenantTimeline, RelatedRecordsPanel } from "@/components/atlas/AtlasContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StatusBadge from "@/components/app/StatusBadge";
import RecordTimeline from "@/components/app/RecordTimeline";
import RecordComments from "@/components/app/RecordComments";
import QuickPreviewDrawer from "@/components/app/QuickPreviewDrawer";
import { MasterActions } from "@/components/app/MasterActions";
import { PincodeField } from "@/components/app/PincodeField";
import CopyQuoteNo from "@/components/commercial/CopyQuoteNo";
import { useSidekick } from "@/components/app/OneSidekick";
import {
  Building2, ArrowLeft, Sparkles, FileText, PackageSearch, ReceiptText,
  Pencil, Save, X, Mail, Users, Star, Plus, Phone, Trash2, Power, History, Ban,
} from "lucide-react";
import { SendWhatsAppButton } from "@/components/whatsapp/WaComposer";
import GstRegistrationsSection from "@/components/customers/GstRegistrationsSection";
import { RecordIntelStrip } from "@/components/app/RecordIntelStrip";
import { WeeklyDigestCard } from "@/components/customers/WeeklyDigestCard";

const fmtMoney = (n, cur = "INR") => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: cur || "INR", maximumFractionDigits: 0 }).format(n);

export default function CrmCustomer360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sidekick = useSidekick();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(false);
  const [contacts, setContacts] = useState({ procurement_email: "", finance_email: "" });
  const [slots, setSlots] = useState({ quote_contact_id: "", invoice_contact_id: "", delivery_contact_id: "", technical_contact_id: "" });
  const [saving, setSaving] = useState(false);
  const [upsells, setUpsells] = useState([]);
  const [editCustomer, setEditCustomer] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/vtiger/crm-customers/${id}/360`)
      .then(({ data }) => {
        setData(data);
        setContacts({
          procurement_email: data.customer?.procurement_email || "",
          finance_email: data.customer?.finance_email || "",
        });
        setSlots({
          quote_contact_id: data.customer?.quote_contact_id || "",
          invoice_contact_id: data.customer?.invoice_contact_id || "",
          delivery_contact_id: data.customer?.delivery_contact_id || "",
          technical_contact_id: data.customer?.technical_contact_id || "",
        });
      })
      .catch(() => toast.error("Could not load customer"))
      .finally(() => setLoading(false));
    api.get(`/applecare/upsell/opportunities`, { params: { customer_id: id } })
      .then(({ data }) => setUpsells(data.items || [])).catch(() => setUpsells([]));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const saveSlot = async (field, value) => {
    setSlots((s) => ({ ...s, [field]: value }));
    try {
      await api.patch(`/vtiger/crm-customers/${id}`, { [field]: value || "" });
      toast.success("Communication contact updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };


  const saveContacts = async () => {
    setSaving(true);
    try {
      await api.patch(`/vtiger/crm-customers/${id}`, contacts);
      toast.success("Communication contacts updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="max-w-[1200px] mx-auto px-4 py-6 space-y-3">
      {[...Array(6)].map((_, i) => <div key={i} className="h-12 bg-slate-50 rounded animate-pulse" />)}
    </div>;
  }
  if (!data) return null;

  const c = data.customer || {};
  const { quotes = [], orders = [], customer_pos = [] } = data;

  return (
    <div className="max-w-[1200px] mx-auto px-4 py-5 space-y-5" data-testid="customer-360-page">
      <button onClick={() => navigate("/commercial/crm/customers")}
        className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900"
        data-testid="customer-360-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Customers
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start gap-4">
        <div className="h-12 w-12 rounded-lg bg-slate-900 text-white grid place-items-center shrink-0">
          <Building2 className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900" data-testid="customer-360-name">
              {c.display_prefix && c.display_prefix !== "None" ? `${c.display_prefix} ` : ""}{c.account_name}
            </h1>
            <button onClick={() => navigate(`/customer-360/${id}`)} data-testid="open-customer-360"
              className="h-7 px-2.5 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800">Open Customer 360°</button>
            <SendWhatsAppButton recipient={{ kind: "customer", id: c.id || id, name: c.account_name, phone: c.phone || c.other_phone, tenant_id: c.master_company_id || id, company: c.account_name }} testid="send-whatsapp-customer" />
            <button onClick={() => navigate(`/commercial/crm/quotations?new=1&customer_id=${c.id || id}&customer_name=${encodeURIComponent(c.account_name || "")}`)} data-testid="customer-quick-quote"
              className="h-7 px-2.5 rounded-md border border-slate-300 bg-white text-[12px] font-medium hover:bg-slate-50">New Quote</button>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-[12px] text-slate-500">
            <span className="font-mono">{c.customer_code || "—"}</span>
            {c.display_name && <span data-testid="customer-360-display-name">· “{c.display_name}”</span>}
            {c.customer_type && <span>· {c.customer_type === "INDIVIDUAL" ? "Individual" : "Organisation"}</span>}
            {c.gstin && <span>· GSTIN {c.gstin}</span>}
            {c.pan && <span>· PAN {c.pan}</span>}
            {c.tax_category && c.tax_category !== "GST_REGISTERED" && <span>· {c.tax_category.replace(/_/g, " ")}</span>}
            {c.billing_city && <span>· <span className="loc-value">{c.billing_city}</span></span>}
            {c.email && <span>· {c.email}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="customer-360-classification">
            {c.customer_type ? (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                {c.customer_type === "INDIVIDUAL" ? "INDIVIDUAL" : "ORGANISATION"}
              </span>
            ) : (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 border border-slate-200">
                ENTITY: UNSET
              </span>
            )}
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
              !c.sales_relationship ? "bg-slate-50 text-slate-500 border-slate-200"
              : (c.sales_relationship || "").includes("DEALER") ? "bg-indigo-50 text-indigo-700 border-indigo-200"
              : (c.sales_relationship === "PROSPECT") ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
              data-testid="customer-360-relationship">
              {c.sales_relationship || "UNCLASSIFIED"}
            </span>
            {c.customer_segment && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200"
                data-testid="customer-360-segment">
                {c.customer_segment}
              </span>
            )}
          </div>
          {c.contact_name && (
            <div className="text-[12px] text-slate-600 mt-0.5" data-testid="customer-360-contact">
              Primary contact: <span className="font-medium text-slate-800">
                {c.contact_title && c.contact_title !== "None" ? `${c.contact_title} ` : ""}{c.contact_name}
              </span>
            </div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {sidekick && (
            <Button variant="outline" data-testid="customer-360-sidekick"
              onClick={() => sidekick.openWith({ etype: "customer", eid: id, title: c.account_name, route: `/commercial/crm/customers/${id}` })}
              className="border-amber-200 text-amber-700 hover:bg-amber-50">
              <Sparkles className="h-4 w-4 mr-1.5" /> Ask Sidekick
            </Button>
          )}
          <MasterActions type="customer" id={id} name={c.account_name}
            active={c.active !== false}
            testid="customer-360"
            onEdit={() => setEditCustomer(true)}
            onChanged={(res) => {
              if (res?.deleted) { toast.success("Customer deleted"); navigate("/commercial/crm/customers"); }
              else load();
            }} />
        </div>
      </div>

      {editCustomer && (
        <EditCustomerDrawer id={id} customer={c}
          onClose={() => setEditCustomer(false)}
          onSaved={() => { setEditCustomer(false); load(); }} />
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-3">
        <Tile icon={FileText} label="Quotations" value={quotes.length} />
        <Tile icon={PackageSearch} label="Orders" value={orders.length} />
        <Tile icon={ReceiptText} label="Customer POs" value={customer_pos.length} />
      </div>

      <RecordIntelStrip kind="customer" id={id} />
      <WeeklyDigestCard accountId={id} />

      {/* Tax & Statutory — GST registrations (authoritative for quotations) */}
      <GstRegistrationsSection accountId={id} accountName={c.account_name} />
      <SearchAliasesSection accountId={id} />

      {/* Contact details */}
      <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="customer-360-contact-details">
        <h2 className="text-[13px] font-semibold text-slate-800 mb-3">Contact details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5 text-[13px]">
          <Detail label="Phone" value={c.phone || c.other_phone} />
          <Detail label="Email" value={c.email || c.email2} />
          <Detail label="Website" value={c.website} />
          <Detail label="GSTIN" value={c.gstin} />
          <Detail label="Billing address" value={<AddressValue street={c.billing_street} city={c.billing_city} state={c.billing_state} code={c.billing_code} country={c.billing_country} />} />
          <Detail label="Shipping address" value={<AddressValue street={c.shipping_street} city={c.shipping_city} state={c.shipping_state} code={c.shipping_code} country={c.shipping_country} />} />
        </div>
      </section>

      {/* People — Primary + other contacts with roles */}
      <CustomerIntroductionStatus customerId={id} />
      <ContactsPanel customerId={id} primary={data.primary_contact}
        contacts={data.contacts || []} onChanged={load} />

      {/* Communication roles — map document types to actual contact people */}
      <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="customer-360-comm-roles">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-slate-500" />
          <h2 className="text-[13px] font-semibold text-slate-800">Communication contacts by document type</h2>
          <span className="text-[11px] text-slate-400">— pick the person for each; quotes/invoices auto-suggest these</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["quote_contact_id", "Quote contact", "New quotations default to this person"],
            ["invoice_contact_id", "Invoice contact", "Invoices & finance notifications"],
            ["delivery_contact_id", "Delivery contact", "Dispatch & delivery coordination"],
            ["technical_contact_id", "Technical contact", "IT / technical coordination"],
          ].map(([field, label, hint]) => (
            <div key={field}>
              <div className="text-[11px] font-medium text-slate-700">{label}</div>
              <div className="text-[10px] text-slate-400 mb-1">{hint}</div>
              <select value={slots[field] || ""} onChange={(e) => saveSlot(field, e.target.value)}
                data-testid={`comm-slot-${field}`}
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
                <option value="">— Not set —</option>
                {(data.contacts || []).filter((ct) => ct.id && ct.active !== false).map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.full_name || ct.name}{ct.designation ? ` · ${ct.designation}` : ""}{ct.email ? ` · ${ct.email}` : ""}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
        {(data.contacts || []).filter((ct) => ct.id && ct.active !== false).length === 0 && (
          <div className="text-[11px] text-slate-400 mt-2">Add contacts in the People panel above to assign them here.</div>
        )}
      </section>

      {/* Communication contacts */}
      <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="customer-360-contacts">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4 text-slate-500" />
          <h2 className="text-[13px] font-semibold text-slate-800">Communication contacts</h2>
          <span className="text-[11px] text-slate-400">— milestone emails route to the right person</span>
          {!editing ? (
            <button onClick={() => setEditing(true)} data-testid="customer-360-edit-contacts"
              className="ml-auto inline-flex items-center gap-1 text-[12px] text-emerald-700 hover:underline">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          ) : (
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => { setEditing(false); setContacts({ procurement_email: c.procurement_email || "", finance_email: c.finance_email || "" }); }}
                className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-900">
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
              <Button size="sm" disabled={saving} onClick={saveContacts} data-testid="customer-360-save-contacts"
                className="bg-emerald-700 hover:bg-emerald-800 h-8">
                <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ContactField label="Procurement email" hint="Order · Dispatch · Delivery updates"
            editing={editing} value={contacts.procurement_email} display={c.procurement_email}
            testId="customer-360-procurement-email"
            onChange={(v) => setContacts((s) => ({ ...s, procurement_email: v }))} />
          <ContactField label="Finance email" hint="Invoice notifications"
            editing={editing} value={contacts.finance_email} display={c.finance_email}
            testId="customer-360-finance-email"
            onChange={(v) => setContacts((s) => ({ ...s, finance_email: v }))} />
        </div>
      </section>

      {/* Records */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecordList title="Quotations" testId="customer-360-quotes" rows={quotes} emptyText="No quotations yet"
          action={<Link to={`/commercial/crm/quotations?customer_id=${id}`} className="text-[11px] text-emerald-700 hover:underline" data-testid="customer-360-quotes-view-all">View all in Quotations →</Link>}
          onRow={(r) => setPreview({ type: "quote", id: r.id, title: r.quote_number || r.code, route: "/commercial/crm/quotations" })}
          render={(r) => ({
            primary: r.quote_number || r.code, secondary: r.subject,
            status: r.status, amount: fmtMoney(r.grand_total_inr ?? r.grand_total, r.currency),
          })} />
        <RecordList title="Orders" testId="customer-360-orders" rows={orders} emptyText="No orders yet"
          onRow={(r) => setPreview({ type: "order", id: r.id, title: r.code, route: `/commercial/one-orders?open=${r.id}` })}
          render={(r) => ({
            primary: r.code, secondary: r.customer_po_number ? `PO ${r.customer_po_number}` : "",
            status: r.status, amount: fmtMoney(r.grand_total_inr),
          })} />
        <RecordList title="Customer POs" testId="customer-360-pos" rows={customer_pos} emptyText="No customer POs yet"
          onRow={(r) => setPreview({ type: "customer_po", id: r.id, title: r.customer_po_number || r.code, route: `/commercial/customer-po?open=${r.id}` })}
          render={(r) => ({
            primary: r.customer_po_number || r.code, secondary: r.po_date || "",
            status: r.status, amount: fmtMoney(r.total_amount_inr),
          })} />
      </div>

      {/* ATLAS · Tenant 360 — ONE activity timeline + related records */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-4" data-testid="customer-360-atlas">
        <section className="border border-slate-200 rounded-lg bg-white p-4">
          <h2 className="text-[13px] font-semibold text-slate-800 mb-3">Activity timeline <span className="text-[10px] font-normal text-slate-500">· one chronological view across sales, communication, orders, finance, assets, service, compliance & documents</span></h2>
          <TenantTimeline accountId={id} tenantId={c.customer_master_company_id} />
          <details className="mt-3"><summary className="text-[11px] text-slate-500 cursor-pointer">Legacy record timeline</summary><RecordTimeline etype="customer" eid={id} /></details>
        </section>
        <section className="border border-slate-200 rounded-lg bg-white p-4">
          <h2 className="text-[13px] font-semibold text-slate-800 mb-3">Related records</h2>
          <RelatedRecordsPanel accountId={id} tenantId={c.customer_master_company_id} />
        </section>
      </div>

      {/* AppleCare opportunities */}
      <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="customer-360-applecare">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-amber-600" />
          <h2 className="text-[13px] font-semibold text-slate-800">AppleCare opportunities</h2>
          <span className="text-[11px] text-slate-400 bg-slate-100 rounded-full px-2">{upsells.length}</span>
        </div>
        {upsells.length === 0 ? (
          <div className="text-[12px] text-slate-400 py-3 text-center">No AppleCare upsell opportunities for this account yet</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {upsells.map((u) => (
              <div key={u.id} className="py-2 flex items-center gap-3" data-testid={`customer-360-upsell-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-slate-900 truncate">{u.product_name || u.family_label}</div>
                  <div className="text-[11px] text-slate-400">{u.upsell_code} · Order {u.order_code} · deadline {u.eligibility_deadline || "—"}</div>
                </div>
                <StatusBadge value={u.eligibility} />
                {u.selected_plan && <span className="text-[12px] font-mono text-slate-600">{fmtMoney(u.selected_plan.amount)}</span>}
                <StatusBadge value={u.status} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Discussion */}
      <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="customer-360-discussion">
        <h2 className="text-[13px] font-semibold text-slate-800 mb-3">Discussion</h2>
        <RecordComments etype="customer" eid={id} entityTitle={c.account_name}
          entityRoute={`/commercial/crm/customers/${id}`} />
      </section>

      <QuickPreviewDrawer target={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

const Tile = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 flex items-center gap-3">
    <div className="h-9 w-9 rounded-md bg-slate-100 grid place-items-center text-slate-500"><Icon className="h-4.5 w-4.5" /></div>
    <div>
      <div className="text-xl font-bold text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    </div>
  </div>
);

const ContactField = ({ label, hint, editing, value, display, onChange, testId }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wide text-slate-400">{label}</label>
    {editing ? (
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="name@customer.com"
        className="h-9 mt-1" data-testid={testId} />
    ) : (
      <div className="text-[13px] text-slate-800 mt-1 min-h-[20px]" data-testid={testId}>
        {display || <span className="text-slate-400 italic">Not set — falls back to order contact</span>}
      </div>
    )}
    <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>
  </div>
);

const AddressValue = ({ street, city, state, code, country }) => {
  const clean = (v) => v && v !== "None" ? v : "";
  const line1Parts = [];
  if (clean(street)) line1Parts.push(clean(street));
  const cityLine = [clean(city), clean(code)].filter(Boolean).join(" - ");
  const stateCountry = [clean(state), clean(country)].filter(Boolean);
  if (!line1Parts.length && !cityLine && !stateCountry.length) return null;
  return (
    <div className="leading-snug">
      {line1Parts.length > 0 && <div>{line1Parts.join(", ")}</div>}
      {cityLine && <div className="loc-value">{cityLine}</div>}
      {stateCountry.length > 0 && (
        <div>
          {stateCountry.map((v, i) => (
            <span key={i}>{i > 0 && <span className="text-slate-400 font-normal"> | </span>}<span className="loc-value">{v}</span></span>
          ))}
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-slate-800 mt-0.5" data-testid={`detail-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {value ? value : <span className="text-slate-400 italic">Not available</span>}
    </span>
  </div>
);


const RecordList = ({ title, rows, onRow, render, emptyText, testId, action }) => (
  <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <h2 className="text-[13px] font-semibold text-slate-800">{title}</h2>
      <span className="text-[11px] text-slate-400 bg-slate-100 rounded-full px-2">{rows.length}</span>
      {action && <span className="ml-auto">{action}</span>}
    </div>
    {rows.length === 0 ? (
      <div className="text-[12px] text-slate-400 py-4 text-center">{emptyText}</div>
    ) : (
      <div className="divide-y divide-slate-100 -mx-1 max-h-72 overflow-y-auto">
        {rows.map((r) => {
          const v = render(r);
          return (
            <button key={r.id} onClick={() => onRow(r)} data-testid={`${testId}-row-${r.id}`}
              className="w-full text-left px-1 py-2 flex items-center gap-2 hover:bg-slate-50 rounded">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-slate-900 font-mono truncate">{v.primary}</div>
                {v.secondary && <div className="text-[11px] text-slate-500 truncate">{v.secondary}</div>}
              </div>
              {v.status && <StatusBadge value={v.status} />}
              <span className="text-[12px] font-mono text-slate-600 shrink-0">{v.amount}</span>
            </button>
          );
        })}
      </div>
    )}
  </section>
);

function EditCustomerDrawer({ id, customer, onClose, onSaved }) {
  const [f, setF] = useState({
    account_name: customer.account_name || "",
    display_prefix: customer.display_prefix || "None",
    customer_type: customer.customer_type || "ORGANISATION",
    sales_relationship: customer.sales_relationship || "END CUSTOMER",
    customer_segment: customer.customer_segment || "",
    gstin: customer.gstin || "",
    email: customer.email || "",
    phone: customer.phone || "",
    website: customer.website || "",
    contact_title: customer.contact_title || "None",
    contact_name: customer.contact_name || "",
    billing_line1: customer.billing_line1 || "",
    billing_line2: customer.billing_line2 || "",
    billing_line3: customer.billing_line3 || "",
    billing_city: customer.billing_city || "",
    billing_code: customer.billing_code || "",
    billing_state: customer.billing_state || "",
    billing_country: customer.billing_country || "India",
    default_currency: customer.default_currency || "INR",
    default_payment_terms: customer.default_payment_terms || "",
    default_validity_days: customer.default_validity_days || "",
    procurement_email: customer.procurement_email || "",
    finance_email: customer.finance_email || "",
  });
  const [saving, setSaving] = useState(false);
  const [opts, setOpts] = useState({ sales_relationship: [], customer_segment: [] });
  useEffect(() => {
    api.get("/vtiger/crm-customers-classification-options")
      .then(({ data }) => setOpts(data)).catch(() => {});
  }, []);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.account_name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = { ...f, default_validity_days: f.default_validity_days ? Number(f.default_validity_days) : null };
      await api.patch(`/vtiger/crm-customers/${id}`, body);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex justify-end" onClick={onClose} data-testid="customer-edit-drawer">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-heading font-semibold text-sm">Edit Customer</div>
          <button onClick={onClose} className="text-slate-500 text-[13px]" data-testid="customer-edit-close">Close ✕</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Display prefix</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.display_prefix} onChange={(e) => u("display_prefix", e.target.value)} data-testid="customer-edit-prefix">
                <option value="M/s.">M/s.</option><option value="None">None</option>
              </select></div>
            <div><Label>Customer type</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.customer_type} onChange={(e) => u("customer_type", e.target.value)} data-testid="customer-edit-type">
                <option value="ORGANISATION">Organisation</option><option value="INDIVIDUAL">Individual</option>
              </select></div>
          </div>
          <div><Label>Legal / display name *</Label><Input value={f.account_name} onChange={(e) => u("account_name", e.target.value)} data-testid="customer-edit-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Sales relationship</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.sales_relationship} onChange={(e) => u("sales_relationship", e.target.value)} data-testid="customer-edit-relationship">
                {(opts.sales_relationship || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
            <div><Label>Customer segment</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.customer_segment} onChange={(e) => u("customer_segment", e.target.value)} data-testid="customer-edit-segment">
                <option value="">— Select —</option>
                {(opts.customer_segment || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>GSTIN</Label><Input value={f.gstin} onChange={(e) => u("gstin", e.target.value.toUpperCase())} data-testid="customer-edit-gstin" /></div>
            <div><Label>Website</Label><Input value={f.website} onChange={(e) => u("website", e.target.value)} /></div>
            <div><Label>Company email</Label><Input value={f.email} onChange={(e) => u("email", e.target.value)} data-testid="customer-edit-email" /></div>
            <div><Label>Company phone</Label><Input value={f.phone} onChange={(e) => u("phone", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact title</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.contact_title} onChange={(e) => u("contact_title", e.target.value)}>
                {["None","Mr.","Ms.","Mrs.","Dr.","Prof."].map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><Label>Primary contact</Label><Input value={f.contact_name} onChange={(e) => u("contact_name", e.target.value)} data-testid="customer-edit-contact" /></div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 pt-2">Billing address</div>
          <Input value={f.billing_line1} onChange={(e) => u("billing_line1", e.target.value)} placeholder="Address line 1" data-testid="customer-edit-line1" />
          <Input value={f.billing_line2} onChange={(e) => u("billing_line2", e.target.value)} placeholder="Address line 2" />
          <Input value={f.billing_line3} onChange={(e) => u("billing_line3", e.target.value)} placeholder="Address line 3" />
          <div className="grid grid-cols-2 gap-3">
            <PincodeField value={f.billing_code} country={f.billing_country} onChange={(v) => u("billing_code", v)}
              onResolve={(d) => setF((s) => ({ ...s, billing_city: d.district || s.billing_city, billing_state: d.state || s.billing_state, billing_country: d.country || s.billing_country }))}
              testid="customer-edit-pin" />
            <div><Label>City</Label><Input className="loc-value" value={f.billing_city} onChange={(e) => u("billing_city", e.target.value)} /></div>
            <div><Label>State</Label><Input className="loc-value" value={f.billing_state} onChange={(e) => u("billing_state", e.target.value)} /></div>
            <div><Label>Country</Label><Input className="loc-value" value={f.billing_country} onChange={(e) => u("billing_country", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3 pt-2">
            <div><Label>Currency</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.default_currency} onChange={(e) => u("default_currency", e.target.value)}>
                {["INR","USD","EUR","GBP","AED","SGD"].map((x) => <option key={x}>{x}</option>)}
              </select></div>
            <div><Label>Payment terms</Label><Input value={f.default_payment_terms} onChange={(e) => u("default_payment_terms", e.target.value)} /></div>
            <div><Label>Validity days</Label><Input type="number" value={f.default_validity_days} onChange={(e) => u("default_validity_days", e.target.value)} /></div>
            <div><Label>Procurement email</Label><Input value={f.procurement_email} onChange={(e) => u("procurement_email", e.target.value)} /></div>
            <div className="col-span-2"><Label>Finance email</Label><Input value={f.finance_email} onChange={(e) => u("finance_email", e.target.value)} /></div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={save} disabled={saving} data-testid="customer-edit-save">
            {saving ? "Saving…" : "Save & close"}
          </Button>
        </div>
      </div>
    </div>
  );
}


const CONTACT_ROLE_OPTIONS = ["PRIMARY", "SALES", "PROCUREMENT", "IT", "FINANCE",
  "ACCOUNTS", "MANAGEMENT", "DELIVERY", "TECHNICAL", "ADMINISTRATION", "OTHER"];
const SALUTATIONS = ["None", "Mr.", "Ms.", "Mrs.", "Dr.", "Prof.", "Other"];

const emptyContactForm = () => ({
  salutation: "None", first_name: "", middle_name: "", last_name: "",
  designation: "", department: "", contact_roles: [],
  email: "", email2: "", phone: "", mobile: "", alt_phone: "", notes: "",
});

function ContactForm({ form, setForm, onCancel, onSave, busy, saveLabel }) {
  const u = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleRole = (r) => setForm((s) => {
    const has = (s.contact_roles || []).includes(r);
    return { ...s, contact_roles: has ? s.contact_roles.filter((x) => x !== r) : [...(s.contact_roles || []), r] };
  });
  return (
    <div className="mt-3 border-t border-slate-100 pt-3 grid grid-cols-2 gap-2" data-testid="contact-form">
      <div>
        <Label className="text-[10px]">Salutation</Label>
        <select value={form.salutation} onChange={(e) => u("salutation", e.target.value)} className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px]" data-testid="c360-add-salutation">
          {SALUTATIONS.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-[10px]">Designation</Label>
        <Input placeholder="e.g. Purchase Manager" value={form.designation} onChange={(e) => u("designation", e.target.value)} data-testid="c360-add-designation" />
      </div>
      <div><Label className="text-[10px]">First name</Label><Input placeholder="First name" value={form.first_name} onChange={(e) => u("first_name", e.target.value)} data-testid="c360-add-first" /></div>
      <div><Label className="text-[10px]">Middle name</Label><Input placeholder="Middle (optional)" value={form.middle_name} onChange={(e) => u("middle_name", e.target.value)} data-testid="c360-add-middle" /></div>
      <div><Label className="text-[10px]">Last name</Label><Input placeholder="Last name" value={form.last_name} onChange={(e) => u("last_name", e.target.value)} data-testid="c360-add-last" /></div>
      <div><Label className="text-[10px]">Department</Label><Input placeholder="Department" value={form.department} onChange={(e) => u("department", e.target.value)} data-testid="c360-add-department" /></div>
      <div><Label className="text-[10px]">Email</Label><Input placeholder="Email" value={form.email} onChange={(e) => u("email", e.target.value)} data-testid="c360-add-email" /></div>
      <div><Label className="text-[10px]">Alternate email</Label><Input placeholder="Alternate email" value={form.email2} onChange={(e) => u("email2", e.target.value)} data-testid="c360-add-email2" /></div>
      <div><Label className="text-[10px]">Mobile</Label><Input placeholder="Mobile" value={form.mobile} onChange={(e) => u("mobile", e.target.value)} data-testid="c360-add-mobile" /></div>
      <div><Label className="text-[10px]">Alternate phone</Label><Input placeholder="Alternate phone" value={form.alt_phone} onChange={(e) => u("alt_phone", e.target.value)} data-testid="c360-add-altphone" /></div>
      <div className="col-span-2">
        <Label className="text-[10px]">Contact roles</Label>
        <div className="flex flex-wrap gap-1.5 mt-1" data-testid="c360-add-roles">
          {CONTACT_ROLE_OPTIONS.map((r) => {
            const on = (form.contact_roles || []).includes(r);
            return (
              <button key={r} type="button" onClick={() => toggleRole(r)}
                data-testid={`c360-role-${r}`}
                className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${on ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>
                {r}
              </button>
            );
          })}
        </div>
      </div>
      <div className="col-span-2"><Label className="text-[10px]">Notes</Label><Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => u("notes", e.target.value)} data-testid="c360-add-notes" /></div>
      <div className="col-span-2 flex gap-2 mt-1">
        <Button variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={onSave} disabled={busy} data-testid="c360-add-save">
          {busy ? "Saving…" : (saveLabel || "Save contact")}
        </Button>
      </div>
    </div>
  );
}

function ContactHistoryModal({ contactId, name, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/vtiger/crm-contacts/${contactId}/history`); setData(data); }
      catch { setData({ events: [], linked_quotes: [] }); }
    })();
  }, [contactId]);
  const label = (a) => ({ create: "Created", update: "Edited", set_primary: "Set as primary",
    deactivate: "Deactivated", activate: "Activated", delete: "Deleted" }[a] || a);
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="contact-history-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-2xl w-full max-w-lg mt-16">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold">History — {name}</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-[13px]" data-testid="contact-history-close">Close ✕</button>
        </div>
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {!data && <div className="text-[12px] text-slate-400">Loading…</div>}
          {data && data.events.length === 0 && <div className="text-[12px] text-slate-400">No history recorded yet.</div>}
          <div className="space-y-2">
            {(data?.events || []).map((e, i) => (
              <div key={i} className="text-[12px] border-l-2 border-emerald-300 pl-3 py-1" data-testid={`contact-history-event-${i}`}>
                <div className="font-medium text-slate-800">{label(e.action)}</div>
                <div className="text-[10px] text-slate-500">{e.at ? new Date(e.at).toLocaleString() : ""} · {e.by || "system"}</div>
              </div>
            ))}
          </div>
          {(data?.linked_quotes || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Linked quotes</div>
              {data.linked_quotes.map((q) => (
                <div key={q.id} className="text-[12px] text-slate-700 inline-flex items-center gap-1 w-full"><span className="font-mono">{q.code}</span><CopyQuoteNo quoteNo={q.code} size={12} /> <span className="text-slate-500">— {q.subject || "—"}</span> <span className="text-slate-400">({q.status})</span></div>
              ))}
            </div>
          )}
          {(data?.walkin_feedback || []).length > 0 && (
            <div className="mt-4" data-testid="contact-walkin-feedback">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Walk-in / service feedback (selected company)</div>
              {data.walkin_feedback.map((w) => (
                <div key={w.id} className="text-[12px] text-slate-700 w-full" data-testid={`contact-walkin-${w.id}`}><span className="font-mono">{w.code}</span> <span className="text-slate-500">— {w.description}</span> <span className="text-slate-400">({w.status}{w.response?.rating ? ` · ${w.response.rating}★` : ""})</span></div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function ContactsPanel({ customerId, primary, contacts, onChanged }) {
  const { user } = useAuth();
  const canHardDelete = ["super_admin", "management"].includes(user?.role);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyContactForm());
  const [busy, setBusy] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const formRef = useRef(null);
  useEffect(() => {
    if (adding && formRef.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [adding, editingId]);

  const setPrimary = async (cid) => {
    try { await api.post(`/vtiger/crm-contacts/${cid}/set-primary`); toast.success("Primary contact updated"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (ct) => {
    const action = ct.active === false ? "activate" : "deactivate";
    try { await api.post(`/vtiger/crm-contacts/${ct.id}/${action}`); toast.success(`Contact ${action}d`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeContact = async (ct) => {
    if (!window.confirm(`Permanently delete ${ct.full_name || ct.name}? This is only possible if the contact was never used.`)) return;
    try { await api.delete(`/vtiger/crm-contacts/${ct.id}`); toast.success("Contact deleted"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const startAdd = () => { setForm(emptyContactForm()); setEditingId(null); setAdding(true); };
  const startEdit = (ct) => {
    setForm({
      salutation: ct.salutation || ct.title || "None", first_name: ct.first_name || "",
      middle_name: ct.middle_name || "", last_name: ct.last_name || "",
      designation: ct.designation || "", department: ct.department || "",
      contact_roles: ct.contact_roles && ct.contact_roles.length ? ct.contact_roles : [],
      email: ct.email || "", email2: ct.email2 || "", phone: ct.phone || "",
      mobile: ct.mobile || "", alt_phone: ct.alt_phone || "", notes: ct.notes || "",
    });
    setEditingId(ct.id); setAdding(true);
  };
  const save = async () => {
    if (!(form.first_name.trim() || form.last_name.trim() || form.email.trim())) { toast.error("Give a name or an email"); return; }
    setBusy(true);
    try {
      const payload = { ...form, salutation: form.salutation === "None" ? null : form.salutation,
        contact_roles: form.contact_roles && form.contact_roles.length ? form.contact_roles : undefined };
      if (editingId) {
        await api.patch(`/vtiger/crm-contacts/${editingId}`, payload);
        toast.success("Contact updated");
      } else {
        await api.post("/vtiger/crm-contacts", { customer_id: customerId, ...payload });
        toast.success("Contact added");
      }
      setAdding(false); setEditingId(null); setForm(emptyContactForm()); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const rows = contacts.length ? contacts : (primary && primary.id ? [primary] : []);
  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="customer-360-people">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-slate-500" />
        <h2 className="text-[13px] font-semibold text-slate-800">People</h2>
        <span className="text-[11px] text-slate-400">— primary contact auto-fills every new quote</span>
        <button onClick={startAdd} data-testid="customer-360-add-contact"
          className="ml-auto inline-flex items-center gap-1 text-[12px] text-emerald-700 hover:underline">
          <Plus className="h-3.5 w-3.5" /> Add contact
        </button>
      </div>
      {rows.length === 0 && !adding && (
        <div className="text-[12px] text-slate-400 py-3">No contacts yet. Click Add contact.</div>
      )}
      <div className="space-y-2">
        {rows.map((ct) => {
          const isPrimary = ct.is_primary || (primary && ct.id && ct.id === primary.id);
          const inactive = ct.active === false;
          return (
          <div key={ct.id || "synth"} className={`flex items-center gap-3 border rounded-md px-3 py-2 ${inactive ? "border-slate-100 bg-slate-50 opacity-70" : "border-slate-100"}`}
            data-testid={`customer-360-contact-${ct.id || "synth"}`}>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                {(ct.title || ct.salutation) ? `${ct.title || ct.salutation} ` : ""}{ct.full_name || ct.name || "(unnamed contact)"}
                {isPrimary && <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 inline-flex items-center gap-0.5"><Star className="h-2.5 w-2.5" />PRIMARY</span>}
                {inactive && <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">INACTIVE</span>}
                {(ct.contact_roles || []).filter((r) => r !== "PRIMARY").map((r) => (
                  <span key={r} className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{r}</span>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
                {ct.designation && <span>{ct.designation}</span>}
                {ct.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{ct.email}</span>}
                {(ct.mobile || ct.phone) && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{ct.mobile || ct.phone}</span>}
                <SendWhatsAppButton recipient={{ kind: "contact", id: ct.id, name: ct.full_name || ct.name, phone: ct.mobile || ct.phone, company: ct.company }} label="Send WhatsApp" testid={`send-whatsapp-contact-${ct.id}`} />
              </div>
            </div>
            {ct.id && (
              <div className="flex items-center gap-2 shrink-0">
                {!isPrimary && !inactive && (
                  <button onClick={() => setPrimary(ct.id)} data-testid={`set-primary-${ct.id}`}
                    className="text-[11px] text-emerald-700 hover:underline whitespace-nowrap" title="Set as primary"><Star className="h-3.5 w-3.5" /></button>
                )}
                <button onClick={() => startEdit(ct)} data-testid={`edit-contact-${ct.id}`}
                  className="text-slate-500 hover:text-slate-900" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => setHistoryFor(ct)} data-testid={`history-contact-${ct.id}`}
                  className="text-slate-500 hover:text-slate-900" title="View history"><History className="h-3.5 w-3.5" /></button>
                <button onClick={() => toggleActive(ct)} data-testid={`toggle-active-${ct.id}`}
                  className={inactive ? "text-emerald-600 hover:text-emerald-800" : "text-amber-600 hover:text-amber-800"}
                  title={inactive ? "Activate" : "Deactivate"}>{inactive ? <Power className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}</button>
                {canHardDelete && (
                  <button onClick={() => removeContact(ct)} data-testid={`delete-contact-${ct.id}`}
                    className="text-rose-500 hover:text-rose-700" title="Delete (only if never used)"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>
      {adding && (
        <div ref={formRef}>
          <ContactForm form={form} setForm={setForm} busy={busy}
            saveLabel={editingId ? "Save changes" : "Add contact"}
            onCancel={() => { setAdding(false); setEditingId(null); }} onSave={save} />
        </div>
      )}
      {historyFor && (
        <ContactHistoryModal contactId={historyFor.id} name={historyFor.full_name || historyFor.name}
          onClose={() => setHistoryFor(null)} />
      )}
    </section>
  );
}


/** Tenant Search Aliases — alternative names (DBA / short names) indexed for universal quotation search. */
function SearchAliasesSection({ accountId }) {
  const [aliases, setAliases] = useState(null);
  const [val, setVal] = useState("");
  const load = useCallback(() => api.get(`/vtiger/crm-customers/${accountId}/search-aliases`).then(({ data }) => setAliases(data.search_aliases || [])).catch(() => setAliases([])), [accountId]);
  useEffect(() => { load(); }, [load]);
  const save = async (next) => {
    try { const { data } = await api.put(`/vtiger/crm-customers/${accountId}/search-aliases`, { search_aliases: next }); setAliases(data.search_aliases); toast.success(`Aliases saved · ${data.quotes_reindexed} quotations re-indexed`); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  if (aliases === null) return null;
  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="customer-360-aliases">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">Search</div>
      <h3 className="text-[14px] font-semibold text-slate-900">Tenant search aliases <span className="text-[11px] font-normal text-slate-500">· e.g. GROWRK · PADA VENTURES · PADA — every alias finds this customer's quotations</span></h3>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {aliases.map((a) => <span key={a} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11.5px]" data-testid={`alias-chip-${a}`}>{a}<button type="button" onClick={() => save(aliases.filter((x) => x !== a))} className="text-slate-400 hover:text-rose-700" aria-label={`Remove ${a}`} data-testid={`alias-remove-${a}`}>✕</button></span>)}
        {aliases.length === 0 && <span className="text-[11.5px] text-slate-400">No aliases yet.</span>}
      </div>
      <div className="mt-2 flex gap-1.5">
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { save([...aliases, val.trim()]); setVal(""); } }} placeholder="Add alias and press Enter" className="h-8 flex-1 max-w-sm rounded border border-slate-300 px-2 text-[12px]" data-testid="alias-input" />
        <button type="button" onClick={() => { if (val.trim()) { save([...aliases, val.trim()]); setVal(""); } }} className="h-8 px-3 rounded bg-slate-900 text-white text-[12px]" data-testid="alias-add">Add</button>
      </div>
    </section>
  );
}
